import { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import { sessionsAPI } from '../services/api';
import {
  isOnline,
  saveOfflineGPSPoint,
  getOfflineGPSPoints,
  clearOfflineGPSPoints,
  saveOfflineTrackingState,
  getOfflineTrackingState,
  clearOfflineTrackingState,
  queuePendingSession,
  getPendingSessions,
  removePendingSession,
  getPendingSyncCount,
  hasPendingData
} from '../services/offlineStorage';

/**
 * Custom hook for managing offline mode during delivery tracking
 * 
 * Features:
 * - Detects online/offline status
 * - ALWAYS saves GPS data locally (buffer for reliability)
 * - Supports background tracking (screen lock/screensaver)
 * - Queues session submissions for later sync
 * - Auto-syncs when connection restored
 * - Shows sync status indicators
 */
export const useOfflineMode = () => {
  const [online, setOnline] = useState(isOnline());
  const [syncing, setSyncing] = useState(false);
  const [pendingCount, setPendingCount] = useState(getPendingSyncCount());
  const [lastOfflineAt, setLastOfflineAt] = useState(null);
  const [offlineGPSBuffer, setOfflineGPSBuffer] = useState([]);
  const [wakeLockActive, setWakeLockActive] = useState(false);
  const syncTimeoutRef = useRef(null);
  const syncFnRef = useRef(null);
  const wakeLockRef = useRef(null);
  const gpsSyncIntervalRef = useRef(null);

  // Update pending count
  const updatePendingCount = useCallback(() => {
    setPendingCount(getPendingSyncCount());
  }, []);

  // Sync all pending data to server - defined early so it can be referenced
  const syncPendingData = useCallback(async () => {
    if (!online || syncing) return { success: false, reason: 'not_ready' };
    
    setSyncing(true);
    let syncedSessions = 0;
    let errors = [];

    try {
      // Sync pending sessions
      const pendingSessions = getPendingSessions();
      for (let i = pendingSessions.length - 1; i >= 0; i--) {
        const session = pendingSessions[i];
        try {
          await sessionsAPI.createSession({
            job_id: session.job_id,
            delivery_type: session.delivery_type,
            deliveries_count: session.deliveries_count,
            distance: session.distance,
            duration: session.duration,
            route_path: session.route_path,
            is_partial: session.is_partial,
            delivered_house_ids: session.delivered_house_ids,
            offline_data: {
              queuedAt: session.queuedAt,
              offlineGPSPoints: session.offlineGPSPoints || []
            }
          });
          removePendingSession(i);
          syncedSessions++;
        } catch (error) {
          console.error('Error syncing session:', error);
          errors.push({ type: 'session', index: i, error: error.message });
        }
      }

      // Clear synced GPS points
      if (syncedSessions > 0) {
        clearOfflineGPSPoints();
        setOfflineGPSBuffer([]);
      }

      updatePendingCount();

      if (syncedSessions > 0) {
        toast.success(`Synced ${syncedSessions} offline session${syncedSessions > 1 ? 's' : ''}`, {
          duration: 3000
        });
      }

      return { 
        success: errors.length === 0, 
        syncedSessions, 
        errors 
      };
    } catch (error) {
      console.error('Error during sync:', error);
      return { success: false, reason: 'sync_error', error: error.message };
    } finally {
      setSyncing(false);
    }
  }, [online, syncing, updatePendingCount]);

  // Keep ref updated with latest sync function
  useEffect(() => {
    syncFnRef.current = syncPendingData;
  }, [syncPendingData]);

  // Handle going online
  const handleOnline = useCallback(() => {
    setOnline(true);
    toast.success('Connection restored', {
      description: hasPendingData() ? 'Syncing offline data...' : 'You\'re back online',
      duration: 3000
    });
    
    // Trigger sync after a short delay to ensure stable connection
    if (syncTimeoutRef.current) {
      clearTimeout(syncTimeoutRef.current);
    }
    syncTimeoutRef.current = setTimeout(() => {
      if (syncFnRef.current && hasPendingData()) {
        syncFnRef.current();
      }
    }, 2000);
  }, []);

  // Handle going offline
  const handleOffline = useCallback(() => {
    setOnline(false);
    setLastOfflineAt(new Date());
    toast.warning('You\'re offline', {
      description: 'Tracking will continue. Data will sync when connection returns.',
      duration: 5000
    });
  }, []);

  // Listen for online/offline events
  useEffect(() => {
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    // Initial check
    setOnline(isOnline());
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
      }
    };
  }, [handleOnline, handleOffline]);

  // Request wake lock to keep screen on during tracking (prevents screensaver)
  const requestWakeLock = useCallback(async () => {
    if ('wakeLock' in navigator) {
      try {
        wakeLockRef.current = await navigator.wakeLock.request('screen');
        setWakeLockActive(true);
        console.log('Wake lock acquired - screen will stay on during tracking');
        
        // Re-acquire wake lock if it's released (e.g., when tab becomes visible again)
        wakeLockRef.current.addEventListener('release', () => {
          console.log('Wake lock released');
          setWakeLockActive(false);
        });
        
        return true;
      } catch (err) {
        console.warn('Wake lock not available:', err.message);
        return false;
      }
    }
    return false;
  }, []);

  // Release wake lock when tracking stops
  const releaseWakeLock = useCallback(async () => {
    if (wakeLockRef.current) {
      try {
        await wakeLockRef.current.release();
        wakeLockRef.current = null;
        setWakeLockActive(false);
        console.log('Wake lock released');
      } catch (err) {
        console.warn('Error releasing wake lock:', err.message);
      }
    }
  }, []);

  // Re-acquire wake lock when page becomes visible again
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible' && wakeLockActive && !wakeLockRef.current) {
        await requestWakeLock();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [wakeLockActive, requestWakeLock]);

  // Save GPS point - ALWAYS saves locally for reliability, syncs when online
  const saveGPSPoint = useCallback((point, jobId) => {
    const gpsData = {
      lat: point[0],
      lon: point[1],
      accuracy: point.accuracy || null,
      jobId,
      timestamp: new Date().toISOString()
    };

    // ALWAYS save to local storage first (offline-first approach)
    saveOfflineGPSPoint(gpsData);
    setOfflineGPSBuffer(prev => {
      // Keep buffer reasonable size (last 1000 points)
      const newBuffer = [...prev, gpsData];
      return newBuffer.length > 1000 ? newBuffer.slice(-1000) : newBuffer;
    });
    
    return { saved: true, offline: !online, buffered: true };
  }, [online]);

  // Save tracking state (for recovery after app close)
  const saveTrackingState = useCallback((state) => {
    const trackingState = {
      ...state,
      isOffline: !online,
      offlineGPSBuffer: offlineGPSBuffer
    };
    return saveOfflineTrackingState(trackingState);
  }, [online, offlineGPSBuffer]);

  // Restore tracking state (when app opens)
  const restoreTrackingState = useCallback(() => {
    return getOfflineTrackingState();
  }, []);

  // Clear tracking state (after successful completion)
  const clearTrackingState = useCallback(() => {
    setOfflineGPSBuffer([]);
    return clearOfflineTrackingState();
  }, []);

  // Queue session for later sync
  const queueSession = useCallback((sessionData) => {
    const success = queuePendingSession(sessionData);
    if (success) {
      updatePendingCount();
      toast.info('Session saved offline', {
        description: 'Will be synced when connection returns',
        duration: 4000
      });
    }
    return success;
  }, [updatePendingCount]);

  // Get offline GPS points for merging into route
  const getOfflinePoints = useCallback(() => {
    return [...getOfflineGPSPoints(), ...offlineGPSBuffer];
  }, [offlineGPSBuffer]);

  // Merge offline GPS points into route path
  const mergeOfflineRoute = useCallback((currentRoute) => {
    const offlinePoints = getOfflinePoints();
    if (offlinePoints.length === 0) return currentRoute;

    // Convert offline points to route format and merge
    const offlineRoute = offlinePoints.map(p => [p.lat, p.lon]);
    
    // Sort by timestamp and merge
    const allPoints = [...currentRoute];
    offlineRoute.forEach(point => {
      // Avoid duplicates
      const isDuplicate = allPoints.some(
        p => Math.abs(p[0] - point[0]) < 0.00001 && Math.abs(p[1] - point[1]) < 0.00001
      );
      if (!isDuplicate) {
        allPoints.push(point);
      }
    });
    
    return allPoints;
  }, [getOfflinePoints]);

  return {
    // Status
    online,
    syncing,
    pendingCount,
    lastOfflineAt,
    hasPendingData: hasPendingData(),
    
    // GPS tracking
    saveGPSPoint,
    getOfflinePoints,
    mergeOfflineRoute,
    offlineGPSBuffer,
    
    // Session management
    queueSession,
    syncPendingData,
    
    // State persistence
    saveTrackingState,
    restoreTrackingState,
    clearTrackingState,
    
    // Background tracking / Wake lock
    wakeLockActive,
    requestWakeLock,
    releaseWakeLock
  };
};

export default useOfflineMode;
